import { type ClassValue, clsx } from 'clsx';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import queryString from 'query-string';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toQueryString(url: string, query?: Record<string, unknown>): string {
  if (!query || Object.keys(query).length === 0) return url;

  const encodedQuery = Object.entries(query).reduce<Record<string, string>>((acc, [key, value]) => {
    if (typeof value === 'object' && value !== null) {
      acc[key] = JSON.stringify(value);
    } else {
      acc[key] = String(value);
    }
    return acc;
  }, {});

  const queryStr = queryString.stringify(encodedQuery, {
    encode: true, // кодувати спецсимволи
    skipNull: true, // пропускати null
    skipEmptyString: true, // пропускати ""
    sort: false, // залишати порядок полів, як у тебе в коді
  });

  return `${url}?${queryStr}`;
}

export function formatNumberCompact(value: number | string): string {
  const number = typeof value === 'string' ? Number(value) : value;
  return Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(number);
}

export function shortenString(str: string, prefixLength: number = 7): string {
  if (!str) return '';
  return str.length > prefixLength ? `${str.slice(0, prefixLength)}...` : str;
}

export function clearFormData<T>(obj: Record<string, any>): T {
  const clean = (value: any): any => {
    if (value === null || value === undefined || value === '' || value === 'none') {
      return undefined;
    }

    if (
      value instanceof File ||
      value instanceof Blob ||
      value instanceof Date ||
      value instanceof FileList
    ) {
      return value;
    }

    if (Array.isArray(value)) {
      const cleanedArray = value.map(clean).filter((v) => v !== undefined);

      return cleanedArray.length ? cleanedArray : undefined;
    }

    if (typeof value === 'object') {
      const result: Record<string, any> = {};

      Object.entries(value).forEach(([k, v]) => {
        const cleaned = clean(v);
        if (cleaned !== undefined) {
          result[k] = cleaned;
        }
      });

      return Object.keys(result).length ? result : undefined;
    }

    return value;
  };

  return clean(obj) as T;
}

export function formatDuration(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '—';

  const start = parseISO(startDate);
  const end = parseISO(endDate);

  const diffMs = differenceInMilliseconds(end, start);

  if (diffMs <= 0) return '0s';

  const seconds = Math.floor(diffMs / 1000) % 60;
  const minutes = Math.floor(diffMs / (1000 * 60)) % 60;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));

  const parts: string[] = [];

  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(' ');
}
